import { useCallback, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import {
  useStore, usePresenters, useChannels, useAssignments, useCanEdit, useTelemetry,
} from '../state/store';
import PresenterCard from '../components/PresenterCard';
import ChannelCard from '../components/ChannelCard';
import AddControl from '../components/AddControl';
import ConflictPanel from '../components/ConflictPanel';
import ConfirmDialog from '../components/ConfirmDialog';
import BoardActions from '../components/BoardActions';
import { useHotkeys, useUndoHotkey } from '../lib/hotkeys';

/**
 * The A2 board: the deck tech's working surface. Unassigned presenters on the
 * left, RF channels on the right, drag to assign.
 */
export default function A2Board({ onEditPresenter }: { onEditPresenter: (id: string) => void }) {
  const identity = useStore(s => s.identity)!;
  const notify = useStore(s => s.notify);
  const conflicts = useStore(s => s.conflicts);
  const presenters = usePresenters();
  const channels = useChannels();
  const assignments = useAssignments();
  const canEdit = useCanEdit();
  const telemetry = useTelemetry();

  /**
   * Match live readings to channels by frequency: it is the one identifier the
   * receiver and the plot genuinely share, since channel numbering on a rack
   * rarely matches the show's TX numbering.
   */
  const rfByChannel = useMemo(() => {
    const m = new Map<string, typeof telemetry[number]>();
    for (const ch of channels) {
      if (ch.frequency === undefined) continue;
      const hit = telemetry.find(t =>
        t.frequency !== undefined && Math.abs(t.frequency - ch.frequency!) < 0.0005);
      if (hit) m.set(ch.id, hit);
    }
    return m;
  }, [channels, telemetry]);

  const [search, setSearch] = useState('');
  const [dragOver, setDragOver] = useState<string | null>(null);
  /** Keyboard flow: pick a presenter, then type a channel number. */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deletingChannel, setDeletingChannel] = useState<string | null>(null);
  const [pendingDigits, setPendingDigits] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const wwbRef = useRef<HTMLInputElement>(null);

  const byId = useMemo(
    () => new Map(presenters.map(p => [p.id, p])), [presenters],
  );
  const assignedIds = useMemo(
    () => new Set(assignments.map(a => a.presenterId)), [assignments],
  );
  const conflictChannels = useMemo(
    () => new Set(conflicts.map(c => c.channelId).filter(Boolean) as string[]), [conflicts],
  );

  const pool = presenters
    .filter(p => !assignedIds.has(p.id))
    .filter(p => {
      // Production calls people by their character and the deck calls them by
      // their name, so searching either has to find them.
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return [p.name, p.characterName, p.title]
        .some(v => v?.toLowerCase().includes(q));
    });

  const guard = () => {
    if (!canEdit) { notify('You joined as a viewer, so the board is read-only.', 'error'); return false; }
    return true;
  };

  async function run(fn: () => Promise<unknown>) {
    try { await fn(); } catch (err) { notify((err as Error).message, 'error'); }
  }

  async function drop(channelId: string, e: React.DragEvent) {
    e.preventDefault();
    setDragOver(null);
    const presenterId = e.dataTransfer.getData('text/presenter-id');
    if (!presenterId || !guard()) return;
    await run(() => api.assign(identity.code, channelId, presenterId));
  }

  async function importFile(file: File, kind: 'rundown' | 'wwb') {
    const text = await file.text();
    await run(async () => {
      if (kind === 'wwb') {
        const r = await api.importWWB(identity.code, text);
        notify(`Imported ${r.imported} channel(s) from Wireless Workbench.`);
      } else {
        const r = await api.importRundown(identity.code, text);
        notify(`Imported ${r.presenters} presenter(s) and ${r.cues} cue(s).`);
      }
    });
  }

  const selected = pool.find(p => p.id === selectedId) ?? null;

  const assignToNumber = useCallback((num: number) => {
    if (!selectedId) return;
    const ch = channels.find(c => c.number === num);
    if (!ch) { notify(`There is no channel ${num}.`, 'error'); return; }
    if (!guard()) return;
    void run(async () => {
      await api.assign(identity.code, ch.id, selectedId);
      setSelectedId(null);
      setPendingDigits('');
    });
  }, [selectedId, channels, identity.code, notify]);

  // Digits accumulate briefly so channels above 9 are reachable; Enter commits
  // early and Escape abandons the whole thing.
  const hotkeys = useMemo(() => {
    const map: Record<string, (e: KeyboardEvent) => void> = {
      Escape: () => { setSelectedId(null); setPendingDigits(''); },
      Enter: () => {
        if (pendingDigits) assignToNumber(Number(pendingDigits));
      },
      Backspace: () => setPendingDigits(d => d.slice(0, -1)),
      a: () => canEdit && run(async () => {
        const r = await api.autoAssign(identity.code);
        if (!r.unplaced.length) { notify(`Auto-assigned all ${r.placed}.`); return; }
        // Say why, not just how many. See BoardActions for the reasoning.
        const why = r.conflicts?.[0]?.message;
        notify(
          `Placed ${r.placed}. ${r.unplaced.length} could not be placed.`
          + (why ? ` ${why}` : ''),
          'error',
        );
      }),
      d: () => canEdit && run(async () => {
        await api.deploy(identity.code);
        notify('Deployed.');
      }),
      // Step through the pool without reaching for the mouse.
      j: () => setSelectedId(cycle(pool, selectedId, 1)),
      k: () => setSelectedId(cycle(pool, selectedId, -1)),
    };
    for (let n = 0; n <= 9; n++) {
      map[String(n)] = () => {
        if (!selectedId) return;
        setPendingDigits(prev => {
          const next = prev + String(n);
          // Commit as soon as no higher-numbered channel could match.
          const num = Number(next);
          const ambiguous = channels.some(c => String(c.number).startsWith(next)
            && c.number !== num);
          if (!ambiguous) { assignToNumber(num); return ''; }
          return next;
        });
      };
    }
    return map;
  }, [pool, selectedId, pendingDigits, channels, canEdit, identity.code, assignToNumber, notify]);

  useHotkeys(hotkeys);

  const undoable = useStore(st => !!st.state?.undo);
  const undo = useCallback(() => {
    if (!canEdit || !undoable) return;
    api.undo(identity.code)
      .then(r => notify(`Undid: ${r.undone.label}.`))
      .catch(e => notify((e as Error).message, 'error'));
  }, [canEdit, undoable, identity.code, notify]);
  useUndoHotkey(undo);

  const doomedChannel = channels.find(c => c.id === deletingChannel);

  return (
    <div className="split">
      {/* ------------------------------------------------- presenter pool */}
      <section className="panel col">
        <div className="panel-head">
          <span className="panel-title">Presenters</span>
          <span className="count">{pool.length} unassigned</span>
          <div className="header-spacer" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search…" style={{ width: 130 }} aria-label="Search presenters"
          />
          {/*
            * Hidden rather than disabled for a viewer. A row of controls that
            * can never do anything is just clutter on a screen somebody is
            * using to read the board, which is the whole of a viewer's job.
            */}
          {canEdit && (
            <>
              <AddControl
                label="How many presenters?"
                defaultCount={4}
                onAdd={n => guard() && run(() => api.addPresenters(identity.code, n))}
              />
              <button className="btn sm" onClick={() => fileRef.current?.click()}>
                Import
              </button>
            </>
          )}
          <input ref={fileRef} type="file" hidden accept=".csv,.tsv,.txt"
                 onChange={e => {
                   const f = e.target.files?.[0];
                   if (f) importFile(f, 'rundown');
                   e.target.value = '';
                 }} />
        </div>

        <div
          className="col-body"
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f && guard()) importFile(f, 'rundown');
          }}
        >
          {pool.length === 0 ? (
            <div className="empty">
              <div>{presenters.length ? 'Everyone is assigned.' : 'No presenters yet.'}</div>
              <div style={{ fontSize: 12 }}>Drop a run-of-show file here to import.</div>
            </div>
          ) : (
            pool.map(p => (
              <PresenterCard
                key={p.id} presenter={p} draggable={canEdit}
                selected={p.id === selectedId}
                onSelect={() => setSelectedId(id => (id === p.id ? null : p.id))}
                onEdit={canEdit ? onEditPresenter : undefined}
              />
            ))
          )}
        </div>
      </section>

      {/* ----------------------------------------------------- RF channels */}
      <section className="panel col">
        <div className="panel-head">
          <span className="panel-title">RF Channels</span>
          <span className="count">{assignments.length} / {channels.length}</span>
          <div className="header-spacer" />
          <BoardActions />
          {canEdit && (
            <>
              <AddControl
                label="How many channels?"
                withType
                defaultCount={8}
                onAdd={(n, micType) =>
                  guard() && run(() => api.addChannels(identity.code, n, micType))}
              />
              <button className="btn sm" onClick={() => wwbRef.current?.click()}>
                WWB
              </button>
            </>
          )}
          <input ref={wwbRef} type="file" hidden accept=".csv,.tsv,.txt"
                 onChange={e => {
                   const f = e.target.files?.[0];
                   if (f) importFile(f, 'wwb');
                   e.target.value = '';
                 }} />
        </div>

        <div className="col-body">
          {selected && (
            <div className="banner keyhint" role="status">
              <strong>{selected.name}</strong>
              <span>
                selected — tap a channel, or type its number
                {pendingDigits && <> (waiting on <code>{pendingDigits}</code>…)</>}
                . Esc to cancel.
              </span>
            </div>
          )}

          <ConflictPanel conflicts={conflicts} />

          {channels.length === 0 ? (
            <div className="empty">
              <div>No channels yet.</div>
              <div style={{ fontSize: 12 }}>Add some, or import from Wireless Workbench.</div>
            </div>
          ) : (
            <div className="ch-grid">
              {channels.map(ch => (
                <ChannelCard
                  key={ch.id}
                  channel={ch}
                  assignments={assignments.filter(a => a.channelId === ch.id)}
                  presenterById={byId}
                  canEdit={canEdit}
                  conflicted={conflictChannels.has(ch.id)}
                  dragOver={dragOver === ch.id}
                  onDragOver={e => { e.preventDefault(); setDragOver(ch.id); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={e => drop(ch.id, e)}
                  onPatch={patch => guard() && run(() =>
                    api.updateChannel(identity.code, ch.id, patch))}
                  onUnassign={aid => run(() => api.unassign(identity.code, aid))}
                  telemetry={rfByChannel.get(ch.id)}
                  armed={!!selectedId && canEdit}
                  onTapAssign={() => assignToNumber(ch.number)}
                  onDelete={canEdit ? () => setDeletingChannel(ch.id) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {doomedChannel && (
        <ConfirmDialog
          title={`Remove TX ${doomedChannel.number}?`}
          body="Anyone assigned to it comes off the board. The remaining channels keep their numbers."
          confirmLabel="Remove"
          danger
          onConfirm={() => {
            const id = doomedChannel.id;
            setDeletingChannel(null);
            void run(() => api.deleteChannel(identity.code, id));
          }}
          onCancel={() => setDeletingChannel(null)}
        />
      )}
    </div>
  );
}

/** Move the selection through the pool, wrapping at both ends. */
function cycle(pool: { id: string }[], current: string | null, step: number): string | null {
  if (pool.length === 0) return null;
  const i = pool.findIndex(p => p.id === current);
  if (i === -1) return pool[step > 0 ? 0 : pool.length - 1]!.id;
  return pool[(i + step + pool.length) % pool.length]!.id;
}
